import styled, { keyframes } from "styled-components";
import React, { useEffect, useCallback, useState, useRef } from "react";
import Questions from "./Questions";
import Chart from "react-apexcharts";
import {
  generate_chart_standards_data,
  generate_chart_factor_standards,
  generate_question_standards,
  generate_outcome_question_standards,
  generate_outcome_question_standards_percentiles,
  generate_chart_standards_percentiles,
  generate_chart_factor_standards_percentiles,
  generate_chart_standards_percentiles_factor,
  generate_question_standards_percentiles,
} from "../calculations";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { SET_NEW_NAVIGATION } from "constants/actions";
import Empty from "./components/Empty";
import { filter_data, get_overall_average } from "reports/Tag/calculations";
import Navigation from "./Navigation";
import AICopilot from "./components/AICopilot";
import { chartDownloadOptions } from "constants/chartOptions";

const Benchmark = ({
  data,
  standards,
  navigation,
  categories,
  questions,
  outcomeStandards,
  rule,
  outcomeQ,
  core_data,
}) => {
  const dispatch = useDispatch();
  const [series, setSeries] = useState(null);
  const [options, setOptions] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [factorData, setFactorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overallScore, setOverallScore] = useState();
  const [filteredData, setFilteredData] = useState();
  const [AIChatOpen, setAIChatOpen] = useState(false);
  const [benchmarkMode, setBenchmarkMode] = useState("differences"); // "differences" or "percentiles"

  const { selectedFilters, latestData } = useSelector(
    (state) => ({
      selectedFilters: state.audit?.new_filters,
      latestData: state.audit?.filtered_data,
    }),
    shallowEqual
  );

  console.log(standards)

  useEffect(() => {
    if (latestData && latestData?.length > 0) {
      let filteredData;
      if (latestData?.[0]?.[0]) {
        filteredData = filter_data(latestData[0], selectedFilters);
      } else {
        filteredData = filter_data(latestData, selectedFilters);
      }
      if (
        (Array.isArray(filteredData[0]) && filteredData[0].length > 0) ||
        (!Array.isArray(filteredData[0]) && filteredData.length > 0)
      ) {
        setFilteredData(filteredData);
        setOverallScore(get_overall_average(filteredData).toFixed(1));
      }
    }
  }, [latestData, selectedFilters]);

  useEffect(() => {
    // Set the navigation to the default value
    const defaultNav = {
      selected1: null,
      selected2: null,
      selected3: null,
      factor: null,
      outcome: {
        name: "Culture",
        id: 0,
      },
    };
    dispatch({
      type: SET_NEW_NAVIGATION,
      payload: defaultNav,
    });
  }, []);

  // Data to be displayed when overview is selected and culture
  useEffect(() => {

    if (filteredData && filteredData.length > 0) {
      if (
        navigation?.outcome?.id > 0 &&
        !navigation?.sub_category &&
        navigation?.factor?.id
      ) {
        let data_type = navigation?.outcome;
        // We need to create a new function to map the outcomes question standards
        // to the factorData

        if (navigation.factor?.id) {
          if (benchmarkMode === "percentiles") {
            setFactorData(
              generate_outcome_question_standards_percentiles(
                filteredData,
                outcomeStandards,
                navigation,
                outcomeQ,
                rule
              )
            );
          } else {
            setFactorData(
              generate_outcome_question_standards(
                filteredData,
                outcomeStandards,
                navigation,
                outcomeQ,
                rule
              )
            );
          }
          return;
        }
      }

      if (navigation?.outcome && !navigation.sub_category?.id && filteredData) {
        let data_type = navigation?.outcome;

        // if (data_type?.id > 0 && !outcomeStandards?.overall_average) {
        //   setChartData(null);
        //   return;
        // }

        if (benchmarkMode === "percentiles") {
          setChartData(
            generate_chart_standards_percentiles(
              filteredData,
              standards,
              navigation,
              categories,
              data_type,
              outcomeStandards,
              rule
            )
          );
        } else {
          setChartData(
            generate_chart_standards_data(
              filteredData,
              standards,
              navigation,
              categories,
              data_type,
              outcomeStandards,
              rule
            )
          );
        }
        return;
      }

      if (navigation.sub_category?.id) {
        let data_type = navigation?.outcome;
        // if (data_type?.id > 0 && !outcomeStandards?.overall_average) {
        //   setChartData(null);
        //   return;
        // }

        if (navigation.factor?.id) {
          if (benchmarkMode === "percentiles") {
            setFactorData(
              generate_question_standards_percentiles(
                filteredData,
                standards,
                navigation,
                categories,
                questions,
                rule
              )
            );
          } else {
            setFactorData(
              generate_question_standards(
                filteredData,
                standards,
                navigation,
                categories,
                questions,
                rule
              )
            );
          }
          return;
        }

        if (
          standards?.id &&
          (navigation.sub_category?.id ||
            navigation.sub_category?.name == "Factor overview")
        ) {
          // let data_type = navigation?.outcome;
          // if (!outcomeStandards?.overall_average) {
          //   setChartData(null);
          //   return;
          // }
          if (benchmarkMode === "percentiles") {
            setChartData(
              generate_chart_factor_standards_percentiles(
                filteredData,
                standards,
                navigation,
                categories,
                data_type,
                questions,
                rule
              )
            );
          } else {
            setChartData(
              generate_chart_factor_standards(
                filteredData,
                standards,
                navigation,
                categories,
                data_type,
                questions,
                rule
              )
            );
          }

          return;
        }
      }
    }
  }, [standards, navigation, categories, filteredData, benchmarkMode]);

  // Check if benchmarks are available
  const hasBenchmarks = () => {
    // Basic check for standards existence
    if (!standards || !standards.response) return false;
    
    // Check if we're in outcome mode
    if (navigation?.outcome?.type === "outcome") {
      return outcomeStandards && Object.keys(outcomeStandards).length > 0;
    }
    
    // For culture mode, check multiple possible standard formats
    // Check for overall standards (used in chart views)
    const hasOverallStandards = standards?.response?.overall?.average && standards?.response?.overall?.std;
    
    // Check for questions standards (used in question drill-down views)
    const hasQuestionStandards = standards?.response?.questions && Object.keys(standards.response.questions).length > 0;
    
    // Check for pillar standards (fallback format)
    const hasPillarStandards = standards?.response?.pillars && standards.response.pillars.length > 0;
    
    return hasOverallStandards || hasQuestionStandards || hasPillarStandards;
  };

  // Check if we should show the toggle (when we have benchmarks or in question view)
  const shouldShowToggle = () => {
    const isQuestionView = navigation.factor?.name && navigation.factor?.name !== "Overview";
    // For question views, show toggle if we have question-level standards
    if (isQuestionView) {
      // Check for outcome standards if we're in outcome mode
      if (navigation?.outcome?.id > 0) {
        return outcomeStandards && Object.keys(outcomeStandards).length > 0;
      }
      // Check for regular culture standards
      return (standards?.response?.questions && Object.keys(standards.response.questions).length > 0) ||
             (standards?.response?.pillars && standards.response.pillars.length > 0);
    }
    return hasBenchmarks();
  };

  // Auto-switch to differences mode if in percentiles mode but no benchmarks available
  // Only apply this to chart views, not question drill-down views
  useEffect(() => {
    const isQuestionView = navigation.factor?.name && navigation.factor?.name !== "Overview";
    if (benchmarkMode === "percentiles" && !hasBenchmarks() && !isQuestionView) {
      setBenchmarkMode("differences");
    }
  }, [standards, outcomeStandards, navigation, benchmarkMode]);

  useEffect(() => {
    if (chartData) {
      setLoading(true);
      
      const isPercentileMode = benchmarkMode === "percentiles";
      const valueKey = isPercentileMode ? "percentile" : "diff";
      
      // Filter out null values based on mode
      let validData = isPercentileMode 
        ? chartData.filter(item => item.percentile !== null)
        : chartData.filter(item => item.diff !== null && item.diff !== undefined);
      
      // If no valid data after filtering, reset chart states and stop loading
      if (validData.length === 0) {
        setSeries(null);
        setOptions(null);
        setLoading(false);
        return;
      }
      
      let sorted_data = validData.sort((a, b) => {
        const aValue = a[valueKey] || 0;
        const bValue = b[valueKey] || 0;
        return bValue > aValue ? 1 : -1;
      });
      
      let _series = [
        {
          data: sorted_data.map((item) => {
            const value = item[valueKey] || 0;
            return {
              x: item.name,
              y: isPercentileMode ? value.toFixed(1) : (value === 0 ? 0.1 : value.toFixed(1)),
              id: item.option_id,
            };
          }),
        },
      ];

      let _options = { ...data3.options };
      _options.chart = { ...chartDownloadOptions };
      
      if (isPercentileMode) {
        // For percentiles, use a gradient color scheme
        _options.colors = sorted_data.map((item) => {
          const percentile = item.percentile || 50;
          if (percentile >= 75) return "#27CDA7"; // Green for high percentiles
          if (percentile >= 50) return "#FFA726"; // Orange for medium percentiles  
          return "#F15062"; // Red for low percentiles
        });
      } else {
        // For differences, use the original color scheme
        _options.colors = sorted_data.map((item) =>
          item.diff >= 0 ? "#27CDA7" : "#F15062"
        );
      }
      
      _options.xaxis.categories = sorted_data.map((item) => item.name);

      if (isPercentileMode) {
        _options.yaxis.max = 100;
        _options.yaxis.min = 0;
        _options.yaxis.title = {
          text: "Percentile rank",
          style: {
            fontWeight: 400,
          },
        };
      } else {
        let max = Math.max(...sorted_data.map((item) => item.diff)) + 5;
        let min = Math.min(...sorted_data.map((item) => item.diff));
        _options.yaxis.max = max > 0 ? max : 0;
        _options.yaxis.min = min < 0 ? min : 0;
        _options.yaxis.title = {
          text: "Difference from the benchmark average",
          style: {
            fontWeight: 400,
          },
        };
      }

      _options.dataLabels = {
        enabled: true,
        formatter: function (val, opt) {
          if (isPercentileMode) {
            return val + "%";
          } else {
            if (sorted_data?.[opt?.dataPointIndex]?.diff === 0) {
              return "0";
            }
            return val;
          }
        },
      };

      _options.tooltip = {
        y: {
          formatter: function (val) {
            if (isPercentileMode) {
              return val + "th percentile";
            } else {
              if (val === 0.1) {
                return "0%";
              }
              return val + "%";
            }
          },
          title: {
            formatter: function (seriesName) {
              return "";
            },
          },
        },
      };

      _options.chart.events = {
        dataPointSelection: (event, chartContext, config) => {
          let nav = { ...navigation };
          if (navigation?.sub_category?.name !== "Overview") {
            nav.factor = {
              name: sorted_data[config.dataPointIndex].name,
              id: sorted_data[config.dataPointIndex].option_id,
              dimension: sorted_data[config.dataPointIndex].dimension,
              type: "factor",
            };
          } else {
            nav.sub_category = {
              name: sorted_data[config.dataPointIndex].name,
              id: sorted_data[config.dataPointIndex].option_id,
              type: "cat_option",
            };
          }

          dispatch({
            type: SET_NEW_NAVIGATION,
            payload: nav,
          });
        },
      };

      setSeries(_series);
      setOptions(_options);
      setTimeout(() => {
        setLoading(false);
      }, [1000]);
    } else {
      // If no chartData, reset chart states
      setSeries(null);
      setOptions(null);
    }
  }, [chartData, selectedFilters, benchmarkMode]);

  const renderChart = () => {
    if (loading) {
      return <div>Loading...</div>;
    }

    if (navigation.factor?.name && navigation.factor?.name != "Overview") {
      return (
        <>
          <Questions
            data={factorData}
            navigation={navigation}
            setAIChatOpen={setAIChatOpen}
            read_feedback={core_data?.permissions?.read_feedback}
            benchmarkMode={benchmarkMode}
          />
        </>
      );
    }

    // Safety check to ensure options and series are defined before rendering chart
    if (!options || !series) {
      return <div>Chart data not available</div>;
    }

    return <Chart options={options} series={series} type="bar" width={800} />;
  };

  const hasNoData = () => {
    const isQuestionView = navigation.factor?.name && navigation.factor?.name !== "Overview";
    
    if (isQuestionView) {
      // For question views, check factorData
      if (!factorData || factorData.length === 0) return true;
      
      if (benchmarkMode === "percentiles") {
        return factorData.filter((item) => item.percentile !== null).length === 0;
      } else {
        // In differences mode, check if we have actual data with averages
        return factorData.filter((item) => item.average !== null && item.average !== undefined).length === 0;
      }
    } else {
      // For chart views, check chartData
      if (!chartData || chartData.length === 0) return true;
      
      if (benchmarkMode === "percentiles") {
        return chartData.filter((item) => item.percentile !== null).length === 0;
      } else {
        return chartData.filter((item) => item.diff !== null && item.diff !== undefined && !isNaN(item.diff)).length === 0;
      }
    }
  };

  return (
    <>
      {AIChatOpen && (
        <AICopilot
          open={AIChatOpen}
          setOpen={setAIChatOpen}
          navigation={navigation}
        />
      )}
      <Container>
        <Title>
          {hasBenchmarks() && benchmarkMode === "percentiles" ? "Benchmark percentiles" : "Differences in benchmarks"}
        </Title>
        <Description>
          {hasBenchmarks() && benchmarkMode === "percentiles" 
            ? "The following chart shows the percentile rank for each area compared to the global benchmark distribution."
            : "The following chart shows the difference between the average score for each area and the global benchmark."
          }
        </Description>

        <Navigation outcomes={outcomeQ} core_data={core_data} />
        
        {shouldShowToggle() && (
          <BenchmarkModeToggle>
            <ToggleLabel>Display mode:</ToggleLabel>
            <ToggleButton 
              active={benchmarkMode === "differences"}
              onClick={() => setBenchmarkMode("differences")}
            >
              Differences
            </ToggleButton>
            <ToggleButton 
              active={benchmarkMode === "percentiles"}
              onClick={() => setBenchmarkMode("percentiles")}
            >
              Percentiles
            </ToggleButton>
          </BenchmarkModeToggle>
        )}

        {(!loading && (hasNoData() || !core_data?.standards?.id)) ||
        core_data?.noData ? (
          <Empty />
        ) : (
          renderChart()
        )}
      </Container>
    </>
  );
};

export default Benchmark;

function adjustMin(max, min) {
  // Calculate the initial difference
  let difference = max - min;

  // Find the nearest lower multiple of 10 for the difference, if necessary
  let adjustedDifference = Math.floor(difference / 10) * 10;

  // Adjust min only if it makes min smaller
  min = max - adjustedDifference;

  return min;
}
const fadeIn = keyframes`

    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const Container = styled.div`
  width: 100%;
`;

const IMG2 = styled.img`
  width: 20px;
  margin-left: 10px;
  margin-bottom: -5px;
`;

const Title = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
`;

const AIButton = styled.div`
  background: #2d70e2;
  font-family: "Raleway", sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  padding: 10px 15px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
  cursor: pointer;
  transition: all 0.3s;
  &:hover {
    background: #333;
  }
`;

const Description = styled.div`
  margin-bottom: 30px;
  line-height: 1.4;
`;

const Help = styled.div`
  display: inline;
  padding: 5px 10px;
  border: 1px solid #666d79;
  color: #666d79;
  font-size: 12px;
  font-family: "Raleway";
  margin-bottom: 10px;
  position: absolute;
  right: 0px;
  top: 10px;
  cursor: pointer;
`;

const data3 = {
  series: [
    {
      data: [10, 8, 4.4, -3, -8, -20, -22],
    },
  ],
  options: {
    chart: {
      type: "bar",
      fontFamily: "Poppins, sans-serif",
      foreColor: "#7E7E7E",
      toolbar: {
        show: false,
      },
      width: 800,
      height: 400,
    },
    tooltip: {
      y: {
        formatter: function (val) {
          if (val === 0.1) {
            return "0%";
          }
          return val + "%";
        },
        title: {
          formatter: function (seriesName) {
            return "";
          },
        },
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      show: false,
    },
    grid: {
      padding: {
        bottom: 60,
      },
    },
    plotOptions: {
      bar: {
        distributed: true,
        dataLabels: {
          // Position the label in the center of the bar
          position: "center",
          style: {
            fontSize: "12px",
            colors: ["#000000"],
          },
        },
      },
    },
    colors: [
      "#27CDA7",
      "#27CDA7",
      "#27CDA7",
      "#F15062",
      "#F15062",
      "#F15062",
      "#F15062",
    ],
    xaxis: {
      categories: [
        "Department A",
        "Department B",
        "Department C",
        "Department D",
        "Department E",
        "Department F",
        "Department G",
      ],
      tickPlacement: "on",
      labels: {
        style: {
          fontSize: "9px",
          colors: "#2D70E2",
        },
        rotateAlways: true,
        maxHeight: 200,
      },
    },
    yaxis: {
      min: 0,
      max: 20,
      decimalsInFloat: 1,
      forceNiceScale: true,
      title: {
        text: "Difference from the benchmark average",
        style: {
          fontWeight: 400,
        },
      },
    },
  },
};

const BenchmarkModeToggle = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ToggleLabel = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: #333;
`;

const ToggleButton = styled.button`
  background: ${({ active }) => (active ? "#2d70e2" : "#f0f0f0")};
  color: ${({ active }) => (active ? "white" : "#333")};
  border: 1px solid ${({ active }) => (active ? "#2d70e2" : "#ddd")};
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s;
  
  &:hover {
    background: ${({ active }) => (active ? "#1e5bb8" : "#e0e0e0")};
    border-color: ${({ active }) => (active ? "#1e5bb8" : "#ccc")};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(45, 112, 226, 0.2);
  }
`;
